import type { ReactNode } from 'react'

type LineProps = {
  delay?: string
  accent?: boolean
  /** forward = down/right (default); reverse = up/left */
  reverse?: boolean
  /** Arrow tip at the end of the line. Default true. */
  arrow?: boolean
  className?: string
}

export const GlowLineVertical = ({
  delay = '0s',
  accent = false,
  reverse = false,
  arrow = true,
  className = '',
}: LineProps) => (
  <div
    className={[
      'm2-path-line-v',
      arrow ? 'm2-path-arrow' : '',
      accent ? 'm2-path-line-v--accent' : '',
      reverse ? 'm2-path-line-v--reverse' : '',
      className,
    ]
      .filter(Boolean)
      .join(' ')}
    style={{ animationDelay: delay }}
    aria-hidden
  />
)

export const GlowLineHorizontal = ({
  delay = '0s',
  accent = false,
  reverse = false,
  arrow = true,
  className = '',
}: LineProps) => (
  <div
    className={[
      'm2-path-line-h',
      arrow ? 'm2-path-arrow' : '',
      accent ? 'm2-path-line-h--accent' : '',
      reverse ? 'm2-path-line-h--reverse' : '',
      className,
    ]
      .filter(Boolean)
      .join(' ')}
    style={{ animationDelay: delay }}
    aria-hidden
  />
)

export type PathNodeVariant = 'start' | 'default' | 'branch' | 'end' | 'decision'

export const PathNode = ({
  children,
  variant = 'default',
  className = '',
  delay,
  active = false,
  onClick,
}: {
  children: ReactNode
  variant?: PathNodeVariant
  className?: string
  delay?: string
  active?: boolean
  onClick?: () => void
}) => {
  const style = delay ? { animationDelay: delay } : undefined
  const activeRing = active
    ? 'ring-2 ring-amber-400 ring-offset-2 ring-offset-m2-surface-container shadow-m2-glow'
    : ''
  const interactive = onClick
    ? 'cursor-pointer hover:border-m2-primary hover:shadow-m2-glow focus-visible:outline focus-visible:outline-2 focus-visible:outline-m2-primary'
    : ''

  // Hình thoi (diamond) cho quyết định — cùng nền tối như node default
  if (variant === 'decision') {
    const diamondBtn = (
      <span
        className={`relative z-10 inline-flex h-[5.75rem] w-[5.75rem] sm:h-[6.5rem] sm:w-[6.5rem] items-center justify-center ${interactive}`}
        style={style}
      >
        <span
          className={`absolute inset-[10%] rotate-45 rounded-md border-2 border-[#c9a227]/70 bg-m2-surface-high shadow-m2-glow ${activeRing}`}
          aria-hidden
        />
        <span className="relative z-10 max-w-[4.25rem] text-center text-[11px] sm:text-xs font-bold leading-tight text-m2-on-surface px-1">
          {children}
        </span>
      </span>
    )

    if (onClick) {
      return (
        <button
          type="button"
          onClick={onClick}
          className={`bg-transparent p-0 border-0 inline-flex justify-center ${className}`}
        >
          {diamondBtn}
        </button>
      )
    }
    return <div className={`inline-flex justify-center ${className}`}>{diamondBtn}</div>
  }

  const base =
    'rounded-lg font-bold text-center relative z-10 transition-shadow transition-colors'
  const variants: Record<Exclude<PathNodeVariant, 'decision'>, string> = {
    start:
      'm2-cta-gradient text-white px-4 py-2.5 text-sm shadow-m2-glow animate-m2-pulse',
    default:
      'bg-m2-surface-high border border-m2-primary/40 text-m2-on-surface px-3 py-2.5 text-sm animate-m2-pulse',
    branch:
      'bg-m2-surface-container border border-m2-outline-variant px-2.5 py-2 rounded-m2 font-label text-[11px] sm:text-xs text-m2-on-surface-variant font-semibold min-w-0',
    end: 'bg-m2-secondary text-m2-on-secondary px-4 py-2.5 text-sm shadow-m2-glow animate-m2-pulse',
  }
  const classes = `${base} ${variants[variant]} ${activeRing} ${interactive} ${className}`

  if (onClick) {
    return (
      <button type="button" className={classes} style={style} onClick={onClick}>
        {children}
      </button>
    )
  }

  return (
    <div className={classes} style={style}>
      {children}
    </div>
  )
}
